import { useState } from 'react'
import Select from 'react-select'
import { useSelector } from 'react-redux'
import getText from '../i18n/labels'
import { isEmpty } from '../Utility'

/**
 * Select customizzabile
 *
 * @see https://www.npmjs.com/package/react-select
 *
 * @param {string} label - label che appare sopra la select,
 * @param {array} options - options of the select
 * @param {array} rankedElements - elements to be ranked in the select
 * @param {function} onChange - funzione di callback quando il valore cambia
 * @param {string} error - messaggio di errore
 * @param {boolean} touched - se true mostra il messaggio di errore
 * @param {string} value - valore selezionato (deve essere il valore dell'oggetto dentro optionValue)
 * @param {string} className - classe aggiuntiva
 * @param {boolean} isCell - se true rende la select più piccola (ideale se vuoi metterla nella tabella)
 * @param {boolean} isSearchable - abilita la ricerca
 * @param {boolean} isClearable - mostra la x per sbiancare il campo
 * @param {boolean} disabled - se true disabilita la select
 * @param {string} optionValue - nome del campo value dell'oggetto -> campo univoco per riconoscere l'oggetto
 * @param {string} optionCode - nome del campo codice dell'oggetto -> serve per mostrare il codice nella select
 * @param {string} optionFlag - nome del campo flag dell'oggetto -> serve per mostrare la bandierina nella select
 * @param {string} optionLabel - nome del campo label dell'oggetto -> serve per mostrare la label nella select
 *
 */
export const BwmSelect = ({
  label,
  options = [],
  rankedElements = [],
  onChange,
  error,
  touched,
  value,
  className = '',
  isCell = false,
  isSearchable = true,
  isClearable = true,
  disabled,
  optionValue = 'id',
  optionCode = null,
  optionFlag = null,
  optionLabel = 'description'
}) => {
  const inputWrapperClass = `select-wrapper w-100 ${touched && error !== undefined ? 'has-error' : ''}`

  const separator = '_'

  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const [isFocused, setIsFocused] = useState(false)

  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color')
    .trim()

  const primaryColorLight = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color-light')
    .trim()

  /**
   * Funzione per formattare l'opzione della select
   * es: aggiungere il codice tramite optionCode
   * es: aggiungere la bandierina tramite optionFlag
   * @param {object} option - l'opzione da formattare
   * @returns {React.ReactElement} - l'opzione formattata
   */
  const formatOptionLabel = (option) => {
    const flag = option?.[optionFlag]
    const showFlag = optionFlag && flag

    const code = option?.[optionCode]
    const showCode = optionCode && code

    if (rankedElements?.length > 0 && option[optionLabel] === separator) {
      return <hr className="m-0 pt-1" />
    }

    return (
      <div title={option[optionLabel]}>
        {showFlag && <span className={`fi fi-${flag?.toString()?.toLowerCase()} me-1`} />}
        {showCode && !showFlag && `${code} - `}
        {option[optionLabel]?.toString()?.toUpperCase()}
      </div>
    )
  }

  /**
   * Funzione per customizzare il tema di React Select
   * E' utilizzata per cambiare il colore principale di React Select
   * con il colore primario definito nel css
   * @param {object} theme - il tema di React Select
   * @returns {object} - il tema personalizzato
   */
  const customTheme = (theme) => {
    return {
      ...theme,
      colors: {
        ...theme.colors,
        primary: primaryColor,
        primary75: primaryColorLight,
        primary50: primaryColorLight,
        primary25: primaryColorLight
      }
    }
  }

  /**
   * Recupera l'oggetto dell'opzione selezionata in base al valore corrente.
   *
   * @returns {object|null} L'oggetto dell'opzione che corrisponde al valore corrente, o null se non viene trovato alcun match o le opzioni sono vuote.
   */
  const getValue = () => {
    if (!options || options?.length === 0) return null
    return options.find(
      (option) => !isEmpty(value) && option?.[optionValue]?.toString() === value?.toString()
    )
  }

  /**
   * Filtra le opzioni della select in base al valore dell'input di ricerca.
   * @param {object} option - l'opzione da filtrare
   * @param {string} inputValue - il valore dell'input di ricerca
   * @returns {boolean} - true se l'opzione contiene il valore dell'input di ricerca, false altrimenti
   */
  const filterOption = (option, inputValue) => {
    const label =
      optionCode && option.data[optionCode]
        ? `${option.data[optionCode]} - ${option.data[optionLabel]}`
        : option.data[optionLabel]
    return label?.toString()?.toLowerCase().includes(inputValue?.toString()?.toLowerCase())
  }

  /**
   * Restituisce le opzioni della select.
   * Se sono presenti elementi rankati, li concatena con le opzioni della select, inserendo un separatore tra i due.
   * @returns {array} Le opzioni della select.
   */
  const getOptions = () => {
    if (rankedElements?.length > 0) {
      const _separator = {}
      _separator[optionValue] = ''
      _separator[optionLabel] = separator
      const filteredOptions = options.filter((option) => {
        return !rankedElements.some((el) => el[optionValue] === option[optionValue])
      })
      return [...rankedElements, _separator, ...filteredOptions]
    }
    return options
  }

  /**
   * Restituisce true se l'opzione deve essere disabilitata, false altrimenti.
   * Se sono presenti elementi rankati, disabilita l'opzione se il suo valore label e' uguale al separatore.
   * Altrimenti non li disabilita.
   * @param {object} option - l'opzione da controllare
   * @returns {boolean} - true se l'opzione deve essere disabilitata, false altrimenti
   */
  const getDisableOption = (option) => {
    if (rankedElements?.length > 0) {
      return option[optionLabel] === separator
    }
    return false
  }

  const hasValue = getValue()

  return (
    <div className={inputWrapperClass}>
      <div title={label} className={` ${isSearchable ? '' : 'not-searchable'} ${className}`}>
        {label && !isCell && <label className="form-label">{label}</label>}
        <Select
          menuPortalTarget={document.body}
          classNamePrefix={isCell ? 'form-control-select-cell' : 'form-control-select'}
          isDisabled={disabled}
          menuPlacement="auto"
          styles={{
            option: (provided) => ({
              ...provided,
              color: 'black',
              ':hover': {
                backgroundColor: primaryColorLight
              }
            }),
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999
            }),
            menu: (base) => ({ ...base, zIndex: 9999999 })
          }}
          value={hasValue ? getValue() : null}
          isSearchable={isSearchable}
          isClearable={isClearable}
          placeholder={null}
          theme={customTheme}
          isOptionDisabled={(option) => getDisableOption(option)}
          noOptionsMessage={() => labels.NO_OPTIONS}
          formatOptionLabel={formatOptionLabel}
          options={getOptions()}
          filterOption={(option, inputValue) => filterOption(option, inputValue)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={onChange}
        />
      </div>
    </div>
  )
}
